import {Button, IconButton} from '@dropbox/dig-components/buttons';
import {Menu} from '@dropbox/dig-components/menu';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Toggletip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  ArchiveLine,
  CheckmarkLine,
  ChevronRightLine,
  DeleteLine,
  GlobeLine,
  LockLine,
  MoreHorizontalLine,
  OpenLine,
} from '@dropbox/dig-icons/assets';
import {useQueryClient} from '@tanstack/react-query';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {growthbookCacheAtom} from 'atoms/growthbook';
import {snackbarAtom} from 'atoms/snackbar';
import {Employee, EmployeeRoleLevel, Workstream, WorkstreamEdit, WorkstreamPrivacy} from 'client';
import {ApiError} from 'client/core/ApiError';
import {Avatar} from 'components/DSYS/Avatar';
import {CancelButton} from 'components/DSYS/CancelButton';
import {Eyebrow} from 'components/DSYS/Eyebrow';
import {Link} from 'components/DSYS/Link';
import {OptionalTooltip} from 'components/DSYS/OptionalTooltip';
import {StatusButton} from 'components/DSYS/StatusButton';
import {StatusChip} from 'components/DSYS/StatusChip';
import {isLinkValid} from 'components/projects/ProjectLinkEditor';
import {LinkIcon, linkToIcon} from 'components/shared/LinkIcon';
import {ROUTE_PATHS} from 'constant';
import {useOutsideClick} from 'hooks/useOutsideClick';
import {t} from 'i18next';
import {useAtomValue, useSetAtom} from 'jotai';
import {useContext, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {toCalendarDate} from 'views/admin/utils';
import {
  useWorkstreamDisplayStatus,
  useWorkstreamEdit,
  useWorkstreamStatus,
} from 'views/workstreams/hooks';
import {EmployeeSelect, MultiEmployeeSelect} from 'views/workstreams/TODODSYSStuff';
import {
  getWorkstreamDri,
  getWorkstreamEmployeeByRole,
  getWorkstreamExecSponsor,
  getWorkstreamUpdateVisualStatus,
  getWorkstreamWorkingGroup,
  toShortDate,
} from 'views/workstreams/utils';
import {WorkstreamArchivedStatus} from 'views/workstreams/WorkstreamArchivedStatus';
import {useShouldShowWorkstreamHealthScore} from 'views/workstreams/workstreamChecks';
import {WorkstreamHealthScore} from 'views/workstreams/WorkstreamHealthScore';
import {getWorkstreamPrivacyLabel} from 'views/workstreams/WorkstreamPrivacyMenu';
import {WorkstreamStatusUpdateModal} from 'views/workstreams/WorkstreamStatusUpdateModal';

import {SidebarDriSplitButton, type SidebarMemberRole} from './SidebarDriSplitButton';
import {SidebarPartOfMenu} from './SidebarPartOfMenu';
import {AddResourceModal, SidebarResourcesMenu} from './SidebarResourcesMenu';
import {CreateSwitchboardZeroStateTrigger} from './SwitchboardZeroState';
import {WorkstreamCustomAudienceSummary} from './WorkstreamCustomAudienceSummary';
import {logWorkstreamDetailModified} from './workstreamDetailAnalytics';
import styles from './WorkstreamDetailsShell.module.css';
import {replaceRoleAssociations, toWorkstreamEditPayload} from './workstreamEditPayload';
import {WorkstreamProductRoadmapButton} from './WorkstreamProductRoadmap';
import {SectionCardTitleStyleContext} from './WorkstreamSectionCard';
import {WorkstreamStaffing} from './WorkstreamStaffing';
import {WorkstreamTimeframeChip} from './WorkstreamTimeframeChip';

type SidebarEditorKey =
  | 'dri'
  | 'reporter'
  | 'exec_sponsor'
  | 'steam_owner'
  | 'working_group'
  | 'privacy'
  | null;

type SidebarSingleRoleKey = 'dri' | 'reporter' | 'exec_sponsor' | 'steam_owner';

type SidebarStatusOverride = {
  status: string;
  created_at: string;
};

type SidebarEditableLink = NonNullable<WorkstreamEdit['links']>[number];

type SidebarResource = Pick<SidebarEditableLink, 'id' | 'url' | 'text' | 'icon'> & {
  isEditable: boolean;
};

const PUBLIC_PRIVACY = WorkstreamPrivacy.PUBLIC;
const PRIVATE_PRIVACY = WorkstreamPrivacy.PRIVATE;
const CUSTOM_PRIVACY = WorkstreamPrivacy.CUSTOM;
const SIDEBAR_EDITOR_RESULTS_WIDTH = 320;
// The archive update shown between clicking Archive and the refetch.
const OPTIMISTIC_ARCHIVE_UPDATE_ID = -1;
const SIDEBAR_EDITOR_RESULTS_OFFSET = -136;
const SIDEBAR_EDITOR_RESULTS_TRIGGER_OFFSET = 6;
const SIDEBAR_PRIVACY_POPOVER_WIDTH = 184;
const SIDEBAR_PRIVACY_POPOVER_OFFSET = 6;
const SIDEBAR_PRIVACY_POPOVER_VIEWPORT_PADDING = 8;
const SIDEBAR_WORKING_GROUP_MIN_RESULTS_VIEWPORT_HEIGHT = 220;
// Synthetic (non-editable) resource rows.
const JIRA_RESOURCE_ID = -2;
const SWITCHBOARD_RESOURCE_ID = -3;

const areEmployeeListsEqual = (
  left: Array<Pick<Employee, 'user_id'>>,
  right: Array<Pick<Employee, 'user_id'>>
) =>
  left.length === right.length &&
  left.every((employee, index) => employee.user_id === right[index]?.user_id);

const getUpdatedDateLabel = (value?: string | null, includeUpdatedPrefix: boolean = true) => {
  if (!value) return null;

  const parsedDate = value.includes('T') ? toCalendarDate(value) : new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return value;

  const shortDate = toShortDate(parsedDate);
  return includeUpdatedPrefix ? t('status_updated_date', {date: shortDate}) : shortDate;
};

const getPrivacyMeta = (privacy?: WorkstreamPrivacy | null) => {
  switch (privacy) {
    case WorkstreamPrivacy.PUBLIC:
      return {
        icon: GlobeLine,
        label: getWorkstreamPrivacyLabel(privacy),
      };
    case WorkstreamPrivacy.PRIVATE:
      return {
        icon: LockLine,
        label: getWorkstreamPrivacyLabel(privacy),
      };
    case WorkstreamPrivacy.CUSTOM:
      return {
        icon: LockLine,
        label: getWorkstreamPrivacyLabel(privacy),
      };
    default:
      return null;
  }
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof ApiError) {
    if (
      error.status < 500 &&
      typeof error.body?.detail === 'string' &&
      error.body.detail.trim().length > 0
    ) {
      return error.body.detail;
    }

    return t('unable_to_save').toString();
  }

  if (error instanceof Error && error.message && error.message !== 'Network Error') {
    return error.message;
  }

  return t('unable_to_save').toString();
};

const buildSidebarResources = (
  workstream: Pick<Workstream, 'jira_key'> & {
    links?: NonNullable<WorkstreamEdit['links']>;
  }
): SidebarResource[] => {
  const resources: SidebarResource[] = [];

  if (workstream.jira_key) {
    resources.push({
      id: JIRA_RESOURCE_ID,
      url: `https://dropbox.atlassian.net/browse/${workstream.jira_key}`,
      text: workstream.jira_key,
      icon: 'jira',
      isEditable: false,
    });
  }

  resources.push(
    ...(workstream.links ?? []).map((link) => ({
      ...link,
      isEditable: true,
    }))
  );
  return resources;
};

const getResourceKind = (resource: SidebarResource) =>
  resource.id === SWITCHBOARD_RESOURCE_ID
    ? 'switchboard'
    : resource.id === JIRA_RESOURCE_ID
      ? 'jira'
      : 'custom';

// Saved links match by id; unsaved ones (no id yet) by their fields.
const isSameLink = (link: SidebarEditableLink, resource: SidebarResource) =>
  resource.id != null
    ? link.id === resource.id
    : link.id == null &&
      link.url === resource.url &&
      link.text === resource.text &&
      link.icon === resource.icon;

const SidebarSection = ({
  label,
  tooltipText,
  children,
  className,
  order,
}: {
  label?: string;
  tooltipText?: string;
  children: React.ReactNode;
  className?: string;
  // Row layout: position within the properties row.
  order?: number;
}) => (
  <section
    className={[styles.sidebarSection, className].filter(Boolean).join(' ')}
    // Row layout: unlabeled cells (status, privacy, …) center on their line.
    style={order === undefined ? undefined : {order, alignSelf: label ? undefined : 'center'}}
  >
    <Box display="inline-flex" alignItems="center" columnGap="4">
      {label && <SidebarLabel>{label}</SidebarLabel>}
      {tooltipText ? <Toggletip title={tooltipText} aria-label={tooltipText} /> : null}
    </Box>
    <div className={styles.sidebarSectionBody}>{children}</div>
  </section>
);

// Uppercase eyebrow by default; quiet sentence-case label where the page opts in
// (SectionCardTitleStyleContext, e.g. the /workstreams detail).
const SidebarLabel = ({children}: {children: React.ReactNode}) =>
  useContext(SectionCardTitleStyleContext) === 'label' ? (
    <Text size="small" isBold color="subtle">
      {children}
    </Text>
  ) : (
    <Eyebrow>{children}</Eyebrow>
  );

const SidebarPlaceholder = ({
  children = '—',
  rowClassName,
}: {
  children?: React.ReactNode;
  rowClassName: string;
}) => (
  <div className={rowClassName}>
    <Text size="small" className={styles.sidebarValueSecondary}>
      {children}
    </Text>
  </div>
);

const SidebarPerson = ({
  employee,
  emptyLabel = '—',
}: {
  employee?: Workstream['employee_associations'][number]['employee'];
  emptyLabel?: React.ReactNode;
}) =>
  employee ? (
    <div className={styles.sidebarPersonRow}>
      <div className={styles.sidebarPersonAvatar}>
        <Avatar user={employee} size="small" />
      </div>
      <Text size="small" className={styles.sidebarValuePrimary}>
        {employee.name}
      </Text>
    </div>
  ) : (
    <SidebarPlaceholder rowClassName={styles.sidebarPersonRow}>{emptyLabel}</SidebarPlaceholder>
  );

const SidebarWorkingGroup = ({
  employees,
}: {
  employees: Workstream['employee_associations'][number]['employee'][];
}) => {
  if (!employees.length) {
    return <SidebarPlaceholder rowClassName={styles.sidebarWorkingGroupRow} />;
  }

  if (employees.length === 1) {
    return <SidebarPerson employee={employees[0]} />;
  }

  const visibleEmployees = employees.slice(0, 4);
  const hiddenCount = employees.length - visibleEmployees.length;

  return (
    <div className={styles.sidebarWorkingGroupRow}>
      <div className={styles.sidebarAvatarStack}>
        {visibleEmployees.map((employee, index) => (
          <div
            key={employee.user_id}
            className={styles.sidebarAvatarStackItem}
            style={{marginLeft: index === 0 ? 0 : -8, zIndex: visibleEmployees.length - index}}
            title={employee.name}
          >
            <Avatar user={employee} size="small" />
          </div>
        ))}
      </div>
      {hiddenCount > 0 ? (
        <Text size="small" className={styles.sidebarValueSecondary}>
          + {hiddenCount} {t('more')}
        </Text>
      ) : null}
    </div>
  );
};

const SidebarEditButton = ({
  ariaLabel,
  onClick,
  children,
}: {
  ariaLabel: string;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    aria-label={ariaLabel}
    className={styles.sidebarEditButton}
    onClick={onClick}
  >
    {children}
  </button>
);

export type WorkstreamCustomPrivacyConfig = {
  selectedEmployees: NonNullable<Workstream['private_employees']>;
  selectedTeams: NonNullable<Workstream['private_teams']>;
  onSave: (
    selectedEmployees: NonNullable<Workstream['private_employees']>,
    selectedTeams: NonNullable<Workstream['private_teams']>
  ) => Promise<void>;
};

export const WorkstreamDetailsSidebar = ({
  workstream,
  healthScoreOpenRequestKey,
  onOpenCustomPrivacy,
  switchboardInResources = false,
  hideStatus = false,
  hideEmptyRoles = false,
  quietArchive = false,
  layout = 'column',
  membersSlot,
  onPeopleClick,
  afterPeople,
  roadmapContentSlot,
  onRoadmapContentVisibilityChange,
}: {
  workstream: Workstream;
  healthScoreOpenRequestKey?: string;
  onOpenCustomPrivacy?: (config: WorkstreamCustomPrivacyConfig) => void;
  // Folds the Switchboard section into Resources (used by /workstreams).
  switchboardInResources?: boolean;
  // /workstreams detail: status lives in the page header.
  hideStatus?: boolean;
  // /workstreams detail: hide unassigned roles behind an "Add person" link.
  hideEmptyRoles?: boolean;
  // /workstreams detail: Archive as a plain text action.
  quietArchive?: boolean;
  // 'row' (/workstreams detail): the same sections and editors as a wrapping
  // properties row under the title instead of a tall rail.
  layout?: 'column' | 'row';
  // Row layout: where the people (DRI, sponsor, …) render, as a labeled list
  // (e.g. the page's "Members" section). Without it they stay in the row.
  membersSlot?: HTMLElement | null;
  // Row layout: show the DRI on the properties line; clicking calls this (e.g.
  // to open the Members section).
  onPeopleClick?: () => void;
  // Row layout: joined onto the end of the people chip (e.g. the host's staffing
  // rollup). Needs onPeopleClick.
  afterPeople?: React.ReactNode;
  roadmapContentSlot?: HTMLElement | null;
  onRoadmapContentVisibilityChange?: (open: boolean) => void;
}) => {
  const dri = getWorkstreamDri(workstream);
  const {isWorkstreamHeadcountInputEnabled, isWorkstreamTimeframesEnabled} =
    useAtomValue(growthbookCacheAtom);
  const {employee: loggedInEmployee} = useAtomValue(loggedInEmployeeAtom);
  const setSnackbar = useSetAtom(snackbarAtom);
  const queryClient = useQueryClient();
  const shouldShowHealthScore = useShouldShowWorkstreamHealthScore(workstream.id);
  const {editWorkstream, isPending: isSavingWorkstream} = useWorkstreamEdit();
  const {createUpdate, deleteUpdate} = useWorkstreamStatus();
  const [activeEditor, setActiveEditor] = useState<SidebarEditorKey>(null);
  const [showAllRoles, setShowAllRoles] = useState(false);
  const isRow = layout === 'row';
  // Portals keep these in this component (same state / editors), just placed
  // in the host's members list.
  const inMembers = (key: string, node: React.ReactNode) =>
    isRow && membersSlot ? createPortal(node, membersSlot, key) : node;
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isArchiveStatusPending, setIsArchiveStatusPending] = useState(false);
  const [isEditingResources, setIsEditingResources] = useState(false);
  const [resourceName, setResourceName] = useState('');
  const [resourceUrl, setResourceUrl] = useState('');
  const [resourceError, setResourceError] = useState<string>();
  // The link the resource form is editing; unset while adding a new one.
  const [editingResource, setEditingResource] = useState<SidebarResource>();
  const [linksOverride, setLinksOverride] = useState<
    NonNullable<WorkstreamEdit['links']> | undefined
  >();
  const [roleOverrides, setRoleOverrides] = useState<
    Partial<Record<SidebarSingleRoleKey, Employee | null>>
  >({});
  const [workingGroupOverride, setWorkingGroupOverride] = useState<Employee[] | undefined>();
  const [privacyOverride, setPrivacyOverride] = useState<WorkstreamPrivacy | undefined>();
  const [statusOverride, setStatusOverride] = useState<SidebarStatusOverride | undefined>();
  const sidebarRailRef = useRef<HTMLDivElement>(null);
  const workingGroupEditorRef = useRef<HTMLDivElement>(null);
  const privacyMenuRef = useRef<HTMLDivElement>(null);
  const [privacyPopoverPosition, setPrivacyPopoverPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const reporter = getWorkstreamEmployeeByRole(workstream, 'reporter');
  const sTeamOwner = getWorkstreamEmployeeByRole(workstream, 'steam_owner');
  const execSponsor = getWorkstreamExecSponsor(workstream);
  const workingGroup = getWorkstreamWorkingGroup(workstream) as Employee[];
  const {
    archiveUpdate,
    latestUpdate: dbLatestUpdate,
    showArchivedChip,
  } = useWorkstreamDisplayStatus(workstream);
  const latestUpdate = statusOverride ?? dbLatestUpdate;
  const latestStatus =
    statusOverride?.status ?? dbLatestUpdate?.status ?? workstream.status ?? 'no_status';
  const displayedDri = Object.prototype.hasOwnProperty.call(roleOverrides, 'dri')
    ? (roleOverrides.dri ?? undefined)
    : dri;
  const displayedReporter = Object.prototype.hasOwnProperty.call(roleOverrides, 'reporter')
    ? (roleOverrides.reporter ?? undefined)
    : reporter;
  const displayedExecSponsor = Object.prototype.hasOwnProperty.call(roleOverrides, 'exec_sponsor')
    ? (roleOverrides.exec_sponsor ?? undefined)
    : execSponsor;
  const displayedSTeamOwner = Object.prototype.hasOwnProperty.call(roleOverrides, 'steam_owner')
    ? (roleOverrides.steam_owner ?? undefined)
    : sTeamOwner;
  const displayedWorkingGroup = workingGroupOverride ?? workingGroup;
  // Distinct people across every role (row layout's members button).
  const memberCount = new Set(
    [
      displayedDri,
      displayedReporter,
      displayedExecSponsor,
      displayedSTeamOwner,
      ...displayedWorkingGroup,
    ]
      .filter((employee): employee is NonNullable<typeof employee> => Boolean(employee))
      .map((employee) => employee.user_id)
  ).size;
  // Hover peek on the row's members button, in the Members section's order.
  // The working group stays out: it can run long, and the Members list has it.
  const memberRoles = [
    {label: t('dri').toString(), person: displayedDri},
    {label: t('exec_sponsor').toString(), person: displayedExecSponsor},
    {label: t('workstream_edit_reporter').toString(), person: displayedReporter},
    {label: t('workstream_role_label_steam_owner').toString(), person: displayedSTeamOwner},
  ].filter((role): role is SidebarMemberRole => Boolean(role.person));
  // Optional roles render when assigned, being edited, or revealed via "Add person".
  const showRole = (key: SidebarEditorKey, hasValue: boolean) =>
    !hideEmptyRoles || showAllRoles || hasValue || activeEditor === key;
  const hiddenRoleCount = [
    showRole('reporter', Boolean(displayedReporter)),
    showRole('exec_sponsor', Boolean(displayedExecSponsor)),
    showRole('steam_owner', Boolean(displayedSTeamOwner)),
    showRole('working_group', displayedWorkingGroup.length > 0),
  ].filter((visible) => !visible).length;
  const hasWorkingGroupChanges = !areEmployeeListsEqual(displayedWorkingGroup, workingGroup);
  const displayedPrivacy = privacyOverride ?? workstream.privacy;
  const privacyMeta = getPrivacyMeta(displayedPrivacy);
  // Row layout: custom privacy reads "Custom"; the audience is in its tooltip.
  const isRowCustomPrivacy = isRow && displayedPrivacy === CUSTOM_PRIVACY;
  const displayedLinks = linksOverride ?? workstream.links ?? [];
  const resources: SidebarResource[] = [
    ...(switchboardInResources && workstream.editor_doc_id
      ? [
          {
            id: SWITCHBOARD_RESOURCE_ID,
            url: ROUTE_PATHS.PLANNING_DOC.replace(':id', workstream.editor_doc_id),
            text: t('switchboard').toString(),
            icon: 'paper',
            isEditable: false,
          },
        ]
      : []),
    ...buildSidebarResources({...workstream, links: displayedLinks}),
  ];
  const hasEditableResources = displayedLinks.length > 0;
  const normalizedResourceUrl =
    resourceUrl.startsWith('http://') || resourceUrl.startsWith('https://')
      ? resourceUrl
      : `https://${resourceUrl}`;
  const isResourceValid = isLinkValid(resourceUrl.trim(), resourceName.trim());
  const canSubmitResource =
    resourceName.trim().length > 0 && resourceUrl.trim().length > 0 && isResourceValid;
  const visibleResourceError =
    resourceUrl.trim().length > 0 && !isResourceValid
      ? t('workstream_resources_invalid').toString()
      : resourceError;
  // The row layout is /workstreams's, so its events carry `v4` like the page's others.
  const logResourceClicked = (type: string, resource?: SidebarResource) =>
    analyticsLogger().logEvent('WORKSTREAM_RESOURCE_CLICKED', {
      type,
      workstream_id: workstream.id,
      ...(resource ? {kind: getResourceKind(resource)} : {}),
      ...(isRow ? {v4: true} : {}),
    });

  // Archive / Unarchive flip the cached workstream before the request (and roll it
  // back on failure), so the chip, history, and button change on click.
  const applyOptimisticWorkstream = async (
    update: (cachedWorkstream: Workstream) => Workstream
  ) => {
    const workstreamQueryKey = ['workstream', workstream.id];
    await queryClient.cancelQueries({queryKey: workstreamQueryKey});
    const snapshot = queryClient.getQueriesData<Workstream>({queryKey: workstreamQueryKey});
    queryClient.setQueriesData<Workstream>({queryKey: workstreamQueryKey}, (cachedWorkstream) =>
      cachedWorkstream ? update(cachedWorkstream) : cachedWorkstream
    );
    return () => snapshot.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
  };

  const handleArchiveClick = async () => {
    if (isArchiveStatusPending) return;
    setIsArchiveStatusPending(true);
    const createdAt = new Date().toISOString();
    // The create endpoint returns the workstream ID, not the new update's, so a
    // placeholder stands in until the refetch brings the real one. The legacy flag
    // covers readers that only check `archived`.
    const rollback = await applyOptimisticWorkstream((cachedWorkstream) => ({
      ...cachedWorkstream,
      archived: true,
      updates: [
        {
          id: OPTIMISTIC_ARCHIVE_UPDATE_ID,
          comment: '',
          status: 'archived',
          workstream_id: workstream.id,
          created_at: createdAt,
          author: loggedInEmployee as EmployeeRoleLevel,
        },
        ...(cachedWorkstream.updates ?? []),
      ],
    }));
    try {
      await createUpdate({
        ownerId: loggedInEmployee.user_id,
        update: {
          comment: '',
          status: 'archived',
          workstream_id: workstream.id,
          created_at: createdAt,
        },
      });

      analyticsLogger().logEvent('WORKSTREAM_MODIFIED', {
        source: 'workstream_details',
        workstream_id: workstream.id,
        property: 'lifecycle',
        type: 'delete',
      });
    } catch {
      rollback();
      setSnackbar({text: t('error_occurred_please_try_again')});
    } finally {
      setIsArchiveStatusPending(false);
    }
  };

  const handleUnarchiveClick = async () => {
    // Legacy archived workstreams will not be shown Unarchive action; a just-made
    // archive has no real id to delete until the refetch lands.
    if (
      isArchiveStatusPending ||
      !archiveUpdate?.id ||
      archiveUpdate.id === OPTIMISTIC_ARCHIVE_UPDATE_ID
    ) {
      return;
    }

    setIsArchiveStatusPending(true);
    const rollback = await applyOptimisticWorkstream((cachedWorkstream) => ({
      ...cachedWorkstream,
      updates: cachedWorkstream.updates?.filter((update) => update.id !== archiveUpdate.id),
    }));
    try {
      await deleteUpdate({id: archiveUpdate.id, workstreamId: workstream.id});
    } catch {
      rollback();
      setSnackbar({text: t('error_occurred_please_try_again')});
    } finally {
      setIsArchiveStatusPending(false);
    }
  };

  const composeSidebarPayload = (
    options: {
      roleUpdates?: Partial<Record<SidebarSingleRoleKey, Employee | null>>;
      workingGroup?: Employee[];
      privacy?: WorkstreamPrivacy;
      links?: WorkstreamEdit['links'];
    } = {}
  ): WorkstreamEdit => {
    const payload = toWorkstreamEditPayload(workstream);
    let employeeAssociations = payload.employee_associations ?? [];
    const effectiveRoleUpdates = {...roleOverrides, ...(options.roleUpdates ?? {})};

    (['dri', 'reporter', 'exec_sponsor', 'steam_owner'] as SidebarSingleRoleKey[]).forEach(
      (roleId) => {
        if (!Object.prototype.hasOwnProperty.call(effectiveRoleUpdates, roleId)) {
          return;
        }

        const employee = effectiveRoleUpdates[roleId];
        employeeAssociations = replaceRoleAssociations(
          employeeAssociations,
          roleId,
          employee ? [employee] : []
        );
      }
    );

    const nextWorkingGroup = Object.prototype.hasOwnProperty.call(options, 'workingGroup')
      ? (options.workingGroup ?? [])
      : workingGroupOverride;
    if (nextWorkingGroup !== undefined) {
      employeeAssociations = replaceRoleAssociations(
        employeeAssociations,
        'working_group',
        nextWorkingGroup
      );
    }

    return {
      ...payload,
      employee_associations: employeeAssociations,
      privacy: options.privacy ?? privacyOverride ?? payload.privacy ?? WorkstreamPrivacy.PUBLIC,
      links: options.links ?? linksOverride ?? payload.links,
    };
  };

  useEffect(() => {
    setRoleOverrides({});
    setWorkingGroupOverride(undefined);
    setPrivacyOverride(undefined);
    setStatusOverride(undefined);
    setLinksOverride(undefined);
  }, [
    workstream.employee_associations,
    workstream.id,
    workstream.links,
    workstream.privacy,
    workstream.updates,
  ]);

  useOutsideClick(
    privacyMenuRef,
    () => {
      setActiveEditor((previous) => (previous === 'privacy' ? null : previous));
    },
    [],
    false,
    'mousedown',
    ['[data-workstream-sidebar-privacy-popover="true"]']
  );

  useOutsideClick(
    workingGroupEditorRef,
    () => {
      if (activeEditor !== 'working_group' || isSavingWorkstream) {
        return;
      }

      if (hasWorkingGroupChanges) {
        void handleWorkingGroupSave();
        return;
      }

      setWorkingGroupOverride(undefined);
      setActiveEditor(null);
    },
    [],
    false,
    'mousedown'
  );

  useEffect(() => {
    if (activeEditor !== 'privacy') {
      setPrivacyPopoverPosition(null);
      return;
    }

    const updatePrivacyPopoverPosition = () => {
      if (!privacyMenuRef.current) {
        return;
      }

      const triggerRect = privacyMenuRef.current.getBoundingClientRect();
      const popover = document.querySelector<HTMLElement>(
        '[data-workstream-sidebar-privacy-popover="true"]'
      );
      const popoverHeight = popover?.offsetHeight ?? 132;
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const shouldOpenUpward =
        spaceBelow <
        popoverHeight + SIDEBAR_PRIVACY_POPOVER_OFFSET + SIDEBAR_PRIVACY_POPOVER_VIEWPORT_PADDING;

      const left = Math.max(
        SIDEBAR_PRIVACY_POPOVER_VIEWPORT_PADDING,
        Math.min(
          triggerRect.right - SIDEBAR_PRIVACY_POPOVER_WIDTH,
          window.innerWidth -
            SIDEBAR_PRIVACY_POPOVER_WIDTH -
            SIDEBAR_PRIVACY_POPOVER_VIEWPORT_PADDING
        )
      );
      const top = shouldOpenUpward
        ? Math.max(
            SIDEBAR_PRIVACY_POPOVER_VIEWPORT_PADDING,
            triggerRect.top - popoverHeight - SIDEBAR_PRIVACY_POPOVER_OFFSET
          )
        : Math.min(
            triggerRect.bottom + SIDEBAR_PRIVACY_POPOVER_OFFSET,
            window.innerHeight - popoverHeight - SIDEBAR_PRIVACY_POPOVER_VIEWPORT_PADDING
          );

      setPrivacyPopoverPosition({top, left});
    };

    updatePrivacyPopoverPosition();
    window.addEventListener('resize', updatePrivacyPopoverPosition);
    window.addEventListener('scroll', updatePrivacyPopoverPosition, true);

    return () => {
      window.removeEventListener('resize', updatePrivacyPopoverPosition);
      window.removeEventListener('scroll', updatePrivacyPopoverPosition, true);
    };
  }, [activeEditor]);

  useEffect(() => {
    if (activeEditor !== 'working_group') {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      const rail = sidebarRailRef.current;
      const editor = workingGroupEditorRef.current;
      const input = editor?.querySelector<HTMLInputElement>('input[name="team-member-typeahead"]');

      if (!rail || !input) {
        return;
      }

      const inputRect = input.getBoundingClientRect();
      const requiredBottom =
        inputRect.bottom +
        SIDEBAR_EDITOR_RESULTS_TRIGGER_OFFSET +
        SIDEBAR_WORKING_GROUP_MIN_RESULTS_VIEWPORT_HEIGHT;
      const overflow = requiredBottom - window.innerHeight;

      if (overflow > 0) {
        rail.scrollBy({top: overflow + SIDEBAR_PRIVACY_POPOVER_VIEWPORT_PADDING});
      }
    });

    return () => cancelAnimationFrame(frameId);
  }, [activeEditor, displayedWorkingGroup.length]);

  const updateWorkstream = async (
    payload: WorkstreamEdit,
    editorToClose: Exclude<SidebarEditorKey, null>
  ) => {
    await editWorkstream({data: payload});
    setActiveEditor((currentEditor) => (currentEditor === editorToClose ? null : currentEditor));
  };

  const handleRoleUpdate = async (roleId: SidebarSingleRoleKey, employee?: Employee) => {
    setRoleOverrides((previous) => ({...previous, [roleId]: employee ?? null}));

    try {
      await updateWorkstream(
        composeSidebarPayload({
          roleUpdates: {[roleId]: employee ?? null},
        }),
        roleId
      );
      logWorkstreamDetailModified({
        workstream_id: workstream.id,
        property: 'employee_roles',
        type: 'edit',
        role: roleId,
      });
    } catch (error) {
      setRoleOverrides((previous) => {
        const next = {...previous};
        delete next[roleId];
        return next;
      });
      throw error;
    }
  };

  const handleWorkingGroupUpdate = (employees: Employee[]) => {
    setWorkingGroupOverride(employees);
  };

  const handleWorkingGroupSave = async () => {
    if (!hasWorkingGroupChanges) {
      setActiveEditor(null);
      return;
    }

    const previousWorkingGroup = workingGroup;
    const payload = composeSidebarPayload({
      workingGroup: displayedWorkingGroup,
    });
    const optimisticEmployeeAssociations = [
      ...workstream.employee_associations.filter(
        ({dri: associationRole}) => associationRole !== 'working_group'
      ),
      ...displayedWorkingGroup.map((employee) => ({
        dri: 'working_group' as const,
        employee,
      })),
    ];
    setActiveEditor(null);

    try {
      await editWorkstream({
        data: payload,
        optimisticEmployeeAssociations,
      });
      logWorkstreamDetailModified({
        workstream_id: workstream.id,
        property: 'working_group',
        type: 'edit',
      });
    } catch (error) {
      setWorkingGroupOverride(previousWorkingGroup);
      setActiveEditor('working_group');
      throw error;
    }
  };

  const handlePrivacyUpdate = async (privacy: WorkstreamPrivacy) => {
    setPrivacyOverride(privacy);

    try {
      await updateWorkstream(
        composeSidebarPayload({
          privacy,
        }),
        'privacy'
      );
      logWorkstreamDetailModified({
        workstream_id: workstream.id,
        property: 'privacy',
        type: 'edit',
      });
    } catch (error) {
      setPrivacyOverride(undefined);
      throw error;
    }
  };

  const handleCustomPrivacyUpdate = async (
    selectedEmployees: NonNullable<Workstream['private_employees']>,
    selectedTeams: NonNullable<Workstream['private_teams']>
  ) => {
    setPrivacyOverride(CUSTOM_PRIVACY);

    try {
      await updateWorkstream(
        {
          ...composeSidebarPayload({
            privacy: CUSTOM_PRIVACY,
          }),
          private_employees: selectedEmployees.map(({user_id}) => user_id),
          private_teams: selectedTeams.map(({team_id}) => team_id),
        },
        'privacy'
      );
      logWorkstreamDetailModified({
        workstream_id: workstream.id,
        property: 'custom_privacy',
        type: 'edit',
      });
    } catch (error) {
      setPrivacyOverride(undefined);
      throw error;
    }
  };

  const resetResourceForm = () => {
    setResourceName('');
    setResourceUrl('');
    setResourceError(undefined);
    setEditingResource(undefined);
  };

  const closeResourceEditor = () => {
    setIsEditingResources(false);
    resetResourceForm();
  };

  const handleSaveResource = async () => {
    if (!canSubmitResource) {
      return;
    }

    const previousName = resourceName;
    const previousUrl = resourceUrl;
    const previousEditingResource = editingResource;
    setResourceError(undefined);

    const url = normalizedResourceUrl.trim();
    const text = resourceName.trim();
    const currentLinks = composeSidebarPayload().links ?? [];
    const nextLinks = (
      editingResource
        ? currentLinks.map((link) =>
            isSameLink(link, editingResource)
              ? // Keep a persisted icon (e.g. from auto-draft) unless the URL changed.
                {...link, url, text, icon: url === link.url ? link.icon : linkToIcon(url)}
              : link
          )
        : [...currentLinks, {url, text, icon: linkToIcon(url)}]
    ).filter((link) => link.url && link.text);
    const payload = composeSidebarPayload({
      links: nextLinks,
    });

    try {
      setLinksOverride(nextLinks);
      closeResourceEditor();
      await editWorkstream({data: payload});
      logResourceClicked(
        previousEditingResource ? 'edit-inline' : 'create-inline',
        previousEditingResource
      );
      logWorkstreamDetailModified({
        workstream_id: workstream.id,
        property: 'links',
        type: previousEditingResource ? 'edit' : 'add',
      });
    } catch (error) {
      setLinksOverride(undefined);
      setIsEditingResources(true);
      setEditingResource(previousEditingResource);
      setResourceName(previousName);
      setResourceUrl(previousUrl);
      setResourceError(getErrorMessage(error));
    }
  };

  const handleDeleteResource = async (resource: SidebarResource) => {
    if (!resource.isEditable) {
      return;
    }

    setResourceError(undefined);
    const currentLinks = composeSidebarPayload().links ?? [];
    const nextLinks = currentLinks.filter((link) => !isSameLink(link, resource));

    try {
      setLinksOverride(nextLinks);
      await editWorkstream({
        data: composeSidebarPayload({
          links: nextLinks,
        }),
      });
      logResourceClicked('delete-inline', resource);
      logWorkstreamDetailModified({
        workstream_id: workstream.id,
        property: 'links',
        type: 'remove',
      });
    } catch (error) {
      setLinksOverride(undefined);
      setResourceError(getErrorMessage(error));
    }
  };

  const openResourceEditor = () => {
    logResourceClicked(hasEditableResources ? 'edit-inline-open' : 'create-inline-open');
    logWorkstreamDetailModified({
      workstream_id: workstream.id,
      property: 'links',
      type: 'open_form',
    });
    setIsEditingResources(true);
  };

  // Row layout: the resources menu's pencil opens the modal on that link.
  const openResourceEditorFor = (resource: SidebarResource) => {
    if (!resource.isEditable) {
      return;
    }

    logResourceClicked('edit-inline-open', resource);
    logWorkstreamDetailModified({
      workstream_id: workstream.id,
      property: 'links',
      type: 'open_form',
    });
    setEditingResource(resource);
    setResourceName(resource.text ?? '');
    setResourceUrl(resource.url);
    setResourceError(undefined);
    setIsEditingResources(true);
  };

  // The archived status's "…" menu (Unarchive). Legacy archived workstreams have
  // no archive update to delete, so no menu.
  const unarchiveMenu = archiveUpdate ? (
    <Menu.Wrapper shouldPropagateClickOutsideMouseEvents>
      {({getContentProps, getTriggerProps}) => (
        <>
          <IconButton
            {...getTriggerProps()}
            variant="borderless"
            size="small"
            className={styles.sidebarArchivedStatusOverflowMenu}
            aria-label={t('more_title').toString()}
            disabled={isArchiveStatusPending}
          >
            <UIIcon src={MoreHorizontalLine} />
          </IconButton>
          <Menu.Content {...getContentProps()} minWidth="160px" placement="bottom-end">
            <Menu.Segment>
              <Menu.ActionItem
                disabled={isArchiveStatusPending}
                onClick={() => void handleUnarchiveClick()}
              >
                {t('unarchive')}
              </Menu.ActionItem>
            </Menu.Segment>
          </Menu.Content>
        </>
      )}
    </Menu.Wrapper>
  ) : null;

  return (
    <>
      {isRow ? (
        <AddResourceModal
          open={isEditingResources && !workstream.archived}
          isEditing={Boolean(editingResource)}
          url={resourceUrl}
          name={resourceName}
          urlInvalid={resourceUrl.trim().length > 0 && !isResourceValid}
          error={visibleResourceError}
          canSubmit={canSubmitResource}
          saving={isSavingWorkstream}
          onUrlChange={(value) => {
            setResourceUrl(value);
            setResourceError(undefined);
          }}
          onNameChange={(value) => {
            setResourceName(value);
            setResourceError(undefined);
          }}
          onCancel={closeResourceEditor}
          onSubmit={() => void handleSaveResource()}
        />
      ) : null}
      <aside
        className={styles.sidebar}
        data-testid="workstream-details-sidebar"
        style={layout === 'row' ? {height: 'auto'} : undefined}
      >
        <div
          ref={sidebarRailRef}
          className={styles.sidebarRail}
          style={
            layout === 'row'
              ? {height: 'auto', overflow: 'visible', paddingLeft: 0, paddingRight: 0}
              : undefined
          }
        >
          <div
            className={styles.sidebarRailContent}
            style={
              isRow
                ? {
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    // Status / readiness / privacy / resources, unlabeled
                    // (they read on their own); people go to membersSlot.
                    alignItems: 'center',
                    // Wraps on narrow panes (worst case ≈ 1,100px: long readiness,
                    // DRI, resources, Part of); names truncate to keep it bounded.
                    columnGap: 24,
                    rowGap: 8,
                  }
                : undefined
            }
          >
            {/* Row: no Archived section; the host's title chip says it instead. */}
            {showArchivedChip && !(isRow && !hideStatus) && (
              <SidebarSection>
                <div className={styles.sidebarArchivedStatusSection}>
                  <WorkstreamArchivedStatus archivedAt={archiveUpdate?.created_at} />
                  {unarchiveMenu}
                </div>
              </SidebarSection>
            )}
            {hideStatus ? null : (
              <SidebarSection
                label={isRow ? undefined : t('status').toString()}
                order={isRow ? 0 : undefined}
              >
                {isRow ? (
                  // Row: the status button itself (the update date lives on the update).
                  // Archiving doesn't change it: the last real status stays, and the
                  // host marks the title as archived instead.
                  <StatusButton
                    status={getWorkstreamUpdateVisualStatus(latestStatus)}
                    size="small"
                    includeArchived
                    onClick={() => setIsStatusModalOpen(true)}
                  />
                ) : (
                  <SidebarEditButton
                    ariaLabel={t('status').toString()}
                    onClick={() => setIsStatusModalOpen(true)}
                  >
                    <div className={styles.sidebarValueBlock}>
                      <StatusChip
                        status={latestStatus}
                        asStatus={getWorkstreamUpdateVisualStatus(latestStatus)}
                        includeArchived
                        size="small"
                      />
                      {latestUpdate && getUpdatedDateLabel(latestUpdate.created_at) ? (
                        <Text
                          size="small"
                          className={`${styles.sidebarValueSecondary} ${styles.sidebarStatusUpdatedText}`}
                        >
                          {getUpdatedDateLabel(latestUpdate.created_at)}
                        </Text>
                      ) : null}
                    </div>
                  </SidebarEditButton>
                )}
              </SidebarSection>
            )}

            {switchboardInResources ? null : (
              <SidebarSection label={t('switchboard').toString()}>
                {workstream.editor_doc_id ? (
                  <Button
                    href={ROUTE_PATHS.PLANNING_DOC.replace(':id', workstream.editor_doc_id)}
                    target="_blank"
                    rel="noreferrer"
                    size="small"
                    variant="outline"
                  >
                    {t('open_switchboard')}
                  </Button>
                ) : (
                  <CreateSwitchboardZeroStateTrigger
                    workstreamId={workstream.id}
                    analyticsSource="details_sidebar_switchboard"
                  />
                )}
              </SidebarSection>
            )}

            {/* Row: the host page shows staffing in its body instead. */}
            {isWorkstreamHeadcountInputEnabled && !isRow ? (
              <WorkstreamStaffing workstreamId={workstream.id} />
            ) : null}

            {/* Row layout sits under the title, whose chip already shows it. */}
            {isWorkstreamTimeframesEnabled && !isRow ? (
              <SidebarSection label="Timeframe">
                <WorkstreamTimeframeChip timeframe={workstream.timeframe} showNone />
              </SidebarSection>
            ) : null}

            {inMembers(
              'dri',
              <SidebarSection label={t('dri').toString()} order={isRow ? 4 : undefined}>
                {activeEditor === 'dri' ? (
                  <div className={styles.sidebarEditorBlock}>
                    <EmployeeSelect
                      selection={displayedDri}
                      setSelection={(employee) => void handleRoleUpdate('dri', employee)}
                      inputClassName={styles.sidebarEmployeeEditor}
                      startEditing
                      onDismiss={() => setActiveEditor(null)}
                      resultsWidth={SIDEBAR_EDITOR_RESULTS_WIDTH}
                      resultsContainerStyle={{marginLeft: SIDEBAR_EDITOR_RESULTS_OFFSET}}
                    />
                  </div>
                ) : (
                  <SidebarEditButton
                    ariaLabel={t('dri').toString()}
                    onClick={() => setActiveEditor('dri')}
                  >
                    <SidebarPerson employee={displayedDri} emptyLabel={t('workstream_no_dri')} />
                  </SidebarEditButton>
                )}
              </SidebarSection>
            )}

            {shouldShowHealthScore ? (
              <SidebarSection
                label={isRow ? undefined : t('health_readiness').toString()}
                order={isRow ? 1 : undefined}
                tooltipText={
                  isRow ? undefined : t('health_readiness_definition_tooltip').toString()
                }
                className={styles.sidebarSectionHideWhenBodyEmpty}
              >
                <div
                  className={
                    isRow
                      ? `${styles.sidebarHealthScore} ${styles.sidebarHealthScoreRow}`
                      : styles.sidebarHealthScore
                  }
                >
                  <WorkstreamHealthScore
                    workstream={workstream}
                    iconSize={24}
                    source="workstream_sidebar"
                    showText
                    openRequestKey={healthScoreOpenRequestKey}
                    headerInfo={
                      isRow ? t('health_readiness_definition_tooltip').toString() : undefined
                    }
                    showSkeletonWhileLoading
                  />
                </div>
              </SidebarSection>
            ) : null}

            {showRole('reporter', Boolean(displayedReporter)) &&
              inMembers(
                'reporter',
                <SidebarSection
                  label={t('workstream_edit_reporter').toString()}
                  order={isRow ? 6 : undefined}
                >
                  {activeEditor === 'reporter' ? (
                    <div className={styles.sidebarEditorBlock}>
                      <EmployeeSelect
                        selection={displayedReporter}
                        setSelection={(employee) => void handleRoleUpdate('reporter', employee)}
                        inputClassName={styles.sidebarEmployeeEditor}
                        startEditing
                        onDismiss={() => setActiveEditor(null)}
                        resultsWidth={SIDEBAR_EDITOR_RESULTS_WIDTH}
                        resultsContainerStyle={{marginLeft: SIDEBAR_EDITOR_RESULTS_OFFSET}}
                        allowEmptySelection
                      />
                    </div>
                  ) : (
                    <SidebarEditButton
                      ariaLabel={t('workstream_edit_reporter').toString()}
                      onClick={() => setActiveEditor('reporter')}
                    >
                      <SidebarPerson employee={displayedReporter} />
                    </SidebarEditButton>
                  )}
                </SidebarSection>
              )}

            {showRole('exec_sponsor', Boolean(displayedExecSponsor)) &&
              inMembers(
                'exec_sponsor',
                <SidebarSection label={t('exec_sponsor').toString()} order={isRow ? 5 : undefined}>
                  {activeEditor === 'exec_sponsor' ? (
                    <div className={styles.sidebarEditorBlock}>
                      <EmployeeSelect
                        selection={displayedExecSponsor}
                        setSelection={(employee) => void handleRoleUpdate('exec_sponsor', employee)}
                        inputClassName={styles.sidebarEmployeeEditor}
                        startEditing
                        onDismiss={() => setActiveEditor(null)}
                        resultsWidth={SIDEBAR_EDITOR_RESULTS_WIDTH}
                        resultsContainerStyle={{marginLeft: SIDEBAR_EDITOR_RESULTS_OFFSET}}
                        allowEmptySelection
                      />
                    </div>
                  ) : (
                    <SidebarEditButton
                      ariaLabel={t('exec_sponsor').toString()}
                      onClick={() => setActiveEditor('exec_sponsor')}
                    >
                      <SidebarPerson employee={displayedExecSponsor} />
                    </SidebarEditButton>
                  )}
                </SidebarSection>
              )}

            {showRole('steam_owner', Boolean(displayedSTeamOwner)) &&
              inMembers(
                'steam_owner',
                <SidebarSection
                  label={t('workstream_role_label_steam_owner').toString()}
                  order={isRow ? 7 : undefined}
                >
                  {activeEditor === 'steam_owner' ? (
                    <div className={styles.sidebarEditorBlock}>
                      <EmployeeSelect
                        selection={displayedSTeamOwner}
                        setSelection={(employee) => void handleRoleUpdate('steam_owner', employee)}
                        inputClassName={styles.sidebarEmployeeEditor}
                        startEditing
                        onDismiss={() => setActiveEditor(null)}
                        resultsWidth={SIDEBAR_EDITOR_RESULTS_WIDTH}
                        resultsContainerStyle={{marginLeft: SIDEBAR_EDITOR_RESULTS_OFFSET}}
                        allowEmptySelection
                      />
                    </div>
                  ) : (
                    <SidebarEditButton
                      ariaLabel={t('workstream_role_label_steam_owner').toString()}
                      onClick={() => setActiveEditor('steam_owner')}
                    >
                      <SidebarPerson employee={displayedSTeamOwner} />
                    </SidebarEditButton>
                  )}
                </SidebarSection>
              )}

            {showRole('working_group', displayedWorkingGroup.length > 0) &&
              inMembers(
                'working_group',
                <SidebarSection label={t('working_group').toString()} order={isRow ? 8 : undefined}>
                  {activeEditor === 'working_group' ? (
                    <div ref={workingGroupEditorRef} className={styles.sidebarEditorBlock}>
                      <MultiEmployeeSelect
                        selections={displayedWorkingGroup}
                        setSelections={handleWorkingGroupUpdate}
                        inputClassName={styles.sidebarEmployeeEditor}
                        startEditing
                        keepInputVisible
                        resultsPlacement="bottom-start"
                        resultsAuto={false}
                        resultsWidth={SIDEBAR_EDITOR_RESULTS_WIDTH}
                        resultsContainerStyle={{marginLeft: SIDEBAR_EDITOR_RESULTS_OFFSET}}
                      />
                      <div
                        className={`${styles.sidebarResourceActions} ${styles.sidebarRightAlignedActions}`}
                      >
                        <CancelButton
                          size="small"
                          className={styles.sidebarInlineActionButton}
                          disabled={isSavingWorkstream}
                          onClick={() => {
                            setWorkingGroupOverride(undefined);
                            setActiveEditor(null);
                          }}
                        >
                          {t('cancel')}
                        </CancelButton>
                        <Button
                          variant="primary"
                          size="small"
                          className={styles.sidebarInlineActionButton}
                          disabled={isSavingWorkstream || !hasWorkingGroupChanges}
                          onClick={() => void handleWorkingGroupSave()}
                        >
                          {t('save')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <SidebarEditButton
                      ariaLabel={t('working_group').toString()}
                      onClick={() => setActiveEditor('working_group')}
                    >
                      <SidebarWorkingGroup employees={displayedWorkingGroup} />
                    </SidebarEditButton>
                  )}
                </SidebarSection>
              )}

            {hiddenRoleCount > 0 && !isRow ? (
              <Button
                variant="transparent"
                size="small"
                className={styles.sidebarAddResourceButton}
                onClick={() => setShowAllRoles(true)}
              >
                {t('workstream_sidebar_add_person')}
              </Button>
            ) : null}

            {/* Row: right after the DRI, before resources / Part of and Archive
                (same order; this comes first in the DOM). */}
            <SidebarSection
              label={isRow ? undefined : t('privacy').toString()}
              order={isRow ? 3 : undefined}
            >
              {privacyMeta ? (
                <div ref={privacyMenuRef} className={styles.sidebarPrivacyMenuRoot}>
                  <OptionalTooltip
                    enabled={isRowCustomPrivacy}
                    title={
                      <>
                        {privacyMeta.label} ·{' '}
                        <WorkstreamCustomAudienceSummary
                          workstream={workstream}
                          privacy={displayedPrivacy}
                          wrapperClassName={atoms({display: 'inline'})}
                          inheritColor
                        />
                      </>
                    }
                  >
                    <button
                      type="button"
                      aria-label={t('privacy').toString()}
                      data-testid={`workstream-sidebar-privacy-${workstream.id}`}
                      className={styles.sidebarEditButton}
                      onClick={() =>
                        setActiveEditor((previous) => (previous === 'privacy' ? null : 'privacy'))
                      }
                    >
                      <div className={styles.sidebarPrivacyButtonContent}>
                        <div
                          className={`${styles.sidebarInlineMetaRow} ${styles.sidebarPrivacyButtonRow}`}
                        >
                          <UIIcon
                            src={privacyMeta.icon}
                            size="small"
                            className={atoms({color: 'Text Subtle'})}
                          />
                          <Text size="small" className={styles.sidebarValuePrimary}>
                            {isRowCustomPrivacy ? 'Custom' : privacyMeta.label}
                          </Text>
                        </div>
                        {isRowCustomPrivacy ? null : (
                          <WorkstreamCustomAudienceSummary
                            workstream={workstream}
                            privacy={displayedPrivacy}
                            className={`${styles.sidebarValueSecondary} ${styles.sidebarPrivacyButtonSubtitle}`}
                            wrapperClassName={styles.sidebarPrivacyButtonSubtitleWrapper}
                          />
                        )}
                      </div>
                    </button>
                  </OptionalTooltip>

                  {activeEditor === 'privacy' && typeof document !== 'undefined'
                    ? createPortal(
                        <div
                          role="menu"
                          className={styles.sidebarPrivacyPopover}
                          data-testid={`workstream-sidebar-privacy-menu-${workstream.id}`}
                          data-workstream-sidebar-privacy-popover="true"
                          style={
                            privacyPopoverPosition
                              ? {
                                  top: privacyPopoverPosition.top,
                                  left: privacyPopoverPosition.left,
                                }
                              : undefined
                          }
                        >
                          {[
                            {
                              value: PUBLIC_PRIVACY,
                              label: getWorkstreamPrivacyLabel(PUBLIC_PRIVACY),
                            },
                            {
                              value: PRIVATE_PRIVACY,
                              label: getWorkstreamPrivacyLabel(PRIVATE_PRIVACY),
                            },
                            {
                              value: CUSTOM_PRIVACY,
                              label: getWorkstreamPrivacyLabel(CUSTOM_PRIVACY),
                            },
                          ].map(({value, label}) => {
                            const isSelected = displayedPrivacy === value;
                            const icon = value === PUBLIC_PRIVACY ? GlobeLine : LockLine;
                            const isCustom = value === CUSTOM_PRIVACY;

                            return (
                              <button
                                key={value}
                                type="button"
                                role="menuitem"
                                className={styles.sidebarPrivacyOption}
                                onClick={() => {
                                  setActiveEditor(null);
                                  if (value === CUSTOM_PRIVACY) {
                                    onOpenCustomPrivacy?.({
                                      selectedEmployees: workstream.private_employees ?? [],
                                      selectedTeams: workstream.private_teams ?? [],
                                      onSave: handleCustomPrivacyUpdate,
                                    });
                                    return;
                                  }

                                  void handlePrivacyUpdate(value);
                                }}
                              >
                                <div className={styles.sidebarPrivacyOptionContent}>
                                  <UIIcon
                                    src={icon}
                                    size="small"
                                    className={atoms({color: 'Text Subtle'})}
                                  />
                                  <Text size="small" className={styles.sidebarValuePrimary}>
                                    {label}
                                  </Text>
                                </div>
                                {isCustom ? (
                                  <UIIcon
                                    src={ChevronRightLine}
                                    size="small"
                                    className={atoms({color: 'Text Subtle'})}
                                  />
                                ) : isSelected ? (
                                  <UIIcon
                                    src={CheckmarkLine}
                                    size="small"
                                    className={atoms({color: 'Text Subtle'})}
                                  />
                                ) : (
                                  <span className={styles.sidebarPrivacyOptionSpacer} />
                                )}
                              </button>
                            );
                          })}
                        </div>,
                        document.body
                      )
                    : null}
                </div>
              ) : (
                <SidebarPlaceholder rowClassName={styles.sidebarInlineMetaRow} />
              )}
            </SidebarSection>

            {isRow && onPeopleClick ? (
              // Row: DRI | member count (| the host's staffing); opens the host's
              // Members section.
              <Box display="flex" alignItems="center" style={{order: 2}}>
                <SidebarDriSplitButton
                  dri={displayedDri}
                  noDriLabel={t('workstream_no_dri').toString()}
                  memberCount={memberCount}
                  roles={memberRoles}
                  onViewMembers={onPeopleClick}
                />
                {afterPeople}
              </Box>
            ) : null}

            {isRow ? (
              <SidebarSection order={3}>
                <Box display="flex" alignItems="center" style={{gap: 8, flexWrap: 'wrap'}}>
                  <WorkstreamProductRoadmapButton
                    key={workstream.id}
                    workstream={workstream}
                    embedSlot={roadmapContentSlot}
                    onRoadmapContentVisibilityChange={onRoadmapContentVisibilityChange}
                  />
                  <SidebarResourcesMenu
                    resources={resources}
                    canEdit={!workstream.archived}
                    disabled={isSavingWorkstream}
                    onAdd={openResourceEditor}
                    onOpen={(resource) => logResourceClicked('open', resource as SidebarResource)}
                    onEdit={(resource) => openResourceEditorFor(resource as SidebarResource)}
                    onRemove={(resource) => void handleDeleteResource(resource as SidebarResource)}
                  />
                  <SidebarPartOfMenu workstreamId={workstream.id} />
                </Box>
                {resourceError && !isEditingResources ? (
                  <Text size="small" color="error">
                    {resourceError}
                  </Text>
                ) : null}
              </SidebarSection>
            ) : (
              <SidebarSection label={t('resources').toString()}>
                {resources.length ? (
                  <div className={styles.sidebarResources}>
                    {resources.map((link) => (
                      <div
                        key={`${link.id ?? 'resource'}-${link.url}-${link.text ?? ''}`}
                        className={styles.sidebarResourceRow}
                      >
                        <Link
                          to={link.url}
                          target="_blank"
                          rel="noreferrer"
                          className={styles.sidebarResourceLink}
                          hasNoUnderline
                        >
                          <div className={styles.sidebarResourceLinkInner}>
                            <div className={styles.sidebarResourceContent}>
                              <LinkIcon
                                icon={link.icon}
                                size="small"
                                className={`${styles.sidebarResourceIcon} ${
                                  (link.icon ?? 'link') === 'link'
                                    ? styles.sidebarResourceIconLink
                                    : ''
                                }`}
                              />
                              <Text size="small" className={styles.sidebarValuePrimary}>
                                {link.text || link.url}
                              </Text>
                            </div>
                            <UIIcon
                              src={OpenLine}
                              size="small"
                              className={styles.sidebarResourceOpenIcon}
                            />
                          </div>
                        </Link>
                        {isEditingResources && link.isEditable ? (
                          <button
                            type="button"
                            aria-label={`${t('remove')} ${link.text || link.url}`}
                            className={styles.sidebarResourceDeleteButton}
                            disabled={isSavingWorkstream}
                            onClick={() => void handleDeleteResource(link)}
                          >
                            <UIIcon src={DeleteLine} size="small" />
                          </button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : null}

                {switchboardInResources && !workstream.editor_doc_id ? (
                  <CreateSwitchboardZeroStateTrigger
                    workstreamId={workstream.id}
                    analyticsSource="details_sidebar_resources"
                  />
                ) : null}

                {!workstream.archived && !isEditingResources ? (
                  <Button
                    variant="transparent"
                    size="small"
                    className={styles.sidebarAddResourceButton}
                    onClick={openResourceEditor}
                  >
                    {hasEditableResources
                      ? t('workstream_edit_resources')
                      : t('workstream_add_resource')}
                  </Button>
                ) : null}

                {!workstream.archived && isEditingResources ? (
                  <div className={styles.sidebarResourceForm}>
                    <div className={styles.sidebarResourceField}>
                      <TextInput
                        size="small"
                        autoFocus
                        placeholder={t('name')}
                        value={resourceName}
                        onChange={(event) => {
                          setResourceName(event.target.value);
                          setResourceError(undefined);
                        }}
                      />
                    </div>
                    <div className={styles.sidebarResourceField}>
                      <TextInput
                        size="small"
                        placeholder={t('url')}
                        value={resourceUrl}
                        isInvalid={resourceUrl.trim().length > 0 && !isResourceValid}
                        onChange={(event) => {
                          setResourceUrl(event.target.value);
                          setResourceError(undefined);
                        }}
                      />
                    </div>
                    <div
                      className={`${styles.sidebarResourceActions} ${styles.sidebarRightAlignedActions}`}
                    >
                      <CancelButton
                        size="small"
                        className={styles.sidebarInlineActionButton}
                        disabled={isSavingWorkstream}
                        onClick={closeResourceEditor}
                      >
                        {t('cancel')}
                      </CancelButton>
                      <Button
                        variant="primary"
                        size="small"
                        className={styles.sidebarInlineActionButton}
                        disabled={!canSubmitResource || isSavingWorkstream}
                        onClick={() => void handleSaveResource()}
                      >
                        {t('save')}
                      </Button>
                    </div>
                    {visibleResourceError ? (
                      <Text size="small" color="error">
                        {visibleResourceError}
                      </Text>
                    ) : null}
                  </div>
                ) : null}
              </SidebarSection>
            )}

            {isRow ? (
              // Row: a labeled action right after the resources / Part of chips;
              // Unarchive once archived. Legacy archived workstreams have no archive
              // update to delete, so no action.
              showArchivedChip && !archiveUpdate ? null : (
                <Box style={{order: 3, alignSelf: 'center'}}>
                  {/* Styled like the row's privacy / people cells beside it (icon,
                      regular-weight label, hover tint), not a bold DIG button. Not
                      disabled while saving: the flip is optimistic, so the handlers
                      just ignore clicks until the request settles. */}
                  <button
                    type="button"
                    className={styles.sidebarEditButton}
                    onClick={() =>
                      void (showArchivedChip ? handleUnarchiveClick() : handleArchiveClick())
                    }
                  >
                    <div className={styles.sidebarInlineMetaRow}>
                      <UIIcon
                        src={ArchiveLine}
                        size="small"
                        className={atoms({color: 'Text Subtle'})}
                      />
                      <Text size="small" className={styles.sidebarValuePrimary}>
                        {showArchivedChip ? t('unarchive') : t('archive')}
                      </Text>
                    </div>
                  </button>
                </Box>
              )
            ) : showArchivedChip ? null : (
              <Button
                variant={quietArchive ? 'transparent' : 'outline'}
                size="small"
                className={quietArchive ? undefined : styles.sidebarArchiveButton}
                withIconStart={<UIIcon src={ArchiveLine} />}
                disabled={isArchiveStatusPending}
                onClick={handleArchiveClick}
              >
                {t('archive')}
              </Button>
            )}
          </div>
        </div>
      </aside>

      {isStatusModalOpen ? (
        <WorkstreamStatusUpdateModal
          workstreamId={workstream.id}
          onClose={() => setIsStatusModalOpen(false)}
          onSubmitted={(result) => setStatusOverride(result)}
          source="workstream-details-sidebar"
        />
      ) : null}
    </>
  );
};
